"use client";

import React, { useContext, useCallback, useRef, useSyncExternalStore, memo } from "react";
import { EditorContext, NodeContext, NodeVisibilityContext } from "../hooks/context";
import type { SerializedNodes, Node } from "../state/types";

interface FrameProps {
  data?: SerializedNodes | string;
}

export function Frame({ data }: FrameProps) {
  const ctx = useContext(EditorContext);
  if (!ctx) {
    throw new Error("<Frame> must be used within an <Editor> component");
  }

  const { store, actions } = ctx;

  // Keep editor state in sync with incoming serialized data
  React.useEffect(() => {
    if (data) {
      actions.deserialize(data);
    }
  }, [data, actions]);

  // Subscribe to state to know when ROOT exists
  const hasRoot = useSyncExternalStore(
    store.subscribe,
    useCallback(() => !!store.getState().nodes["ROOT"], [store]),
    useCallback(() => !!store.getState().nodes["ROOT"], [store]),
  );

  if (!hasRoot) {
    return null;
  }

  return <RenderNode nodeId="ROOT" />;
}

interface NodeSnapshot {
  type: Node["data"]["type"];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>;
  childIds: string[];
  hidden: boolean;
}

const RenderNodeInner = memo(function RenderNodeInner({ nodeId }: { nodeId: string }) {
  const ctx = useContext(EditorContext);
  const visibilityFn = useContext(NodeVisibilityContext);

  const store = ctx?.store ?? null;

  const cachedRef = useRef<NodeSnapshot | null>(null);

  const getSnapshot = useCallback(() => {
    if (!store) return cachedRef.current;
    const node = store.getState().nodes[nodeId];
    if (!node) return cachedRef.current;

    // Only allocate a new snapshot if something actually changed
    const prev = cachedRef.current;
    if (
      prev &&
      prev.type === node.data.type &&
      prev.props === node.data.props &&
      prev.hidden === node.data.hidden &&
      prev.childIds === node.data.nodes
    ) {
      return prev;
    }

    // Check array equality only if references differ
    if (
      prev &&
      prev.type === node.data.type &&
      prev.props === node.data.props &&
      prev.hidden === node.data.hidden &&
      arraysEqual(prev.childIds, node.data.nodes)
    ) {
      return prev;
    }

    const next: NodeSnapshot = {
      type: node.data.type,
      props: node.data.props,
      childIds: node.data.nodes,
      hidden: node.data.hidden,
    };
    cachedRef.current = next;
    return next;
  }, [store, nodeId]);

  const noop = useCallback(() => () => {}, []);
  const subscribe = store?.subscribe ?? noop;
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (!ctx || !snapshot || snapshot.hidden) return null;

  // Consult optional visibility callback (e.g. rule-based visibility filters)
  if (visibilityFn && !visibilityFn(snapshot.props)) return null;

  const Component = snapshot.type;

  // Render children
  const childIds = snapshot.childIds;
  const hasChildren = childIds.length > 0;

  return (
    <NodeContext.Provider value={nodeId}>
      <Component {...snapshot.props}>
        {hasChildren
          ? childIds.map((childId) => <RenderNode key={childId} nodeId={childId} />)
          : undefined}
      </Component>
    </NodeContext.Provider>
  );
});

// Exported RenderNode with stable identity for recursive use
function RenderNode({ nodeId }: { nodeId: string }) {
  return <RenderNodeInner nodeId={nodeId} />;
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export { RenderNode };
